"use client";

import { Link, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useBranchesEnabled } from "@/hooks/api/useBranchesEnabled";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { downloadReport } from "@/lib/report-download";
import { DateRangePicker } from "@/components/reports/DateRangePicker";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ArrowLeft } from "lucide-react";

type SaleRow = Record<string, string | number | null>;

export default function SalesReportPage() {
  const navigate = useNavigate();
  const { branchesEnabled } = useBranchesEnabled();
  const defTo = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const defFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);

  const [from, setFrom] = useState(defFrom);
  const [to, setTo] = useState(defTo);
  const [branchId, setBranchId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [userId, setUserId] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [page, setPage] = useState(1);

  const params = useMemo(
    () => ({
      from_date: from,
      to_date: to,
      page,
      per_page: 25,
      ...(branchId ? { branch_id: Number(branchId) } : {}),
      ...(customerId ? { customer_id: Number(customerId) } : {}),
      ...(userId ? { user_id: Number(userId) } : {}),
      ...(paymentMode ? { payment_mode: paymentMode } : {}),
    }),
    [from, to, page, branchId, customerId, userId, paymentMode],
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["reports", "sales", params],
    queryFn: async () => {
      const { data: d } = await api.get<{
        data: SaleRow[];
        pagination: { total: number; page: number; last_page: number };
        summary: { net: number; taxable: number; cgst: number; sgst: number; igst: number };
      }>("/api/reports/sales", { params });
      return d;
    },
  });

  async function exportXlsx() {
    await downloadReport(
      "/api/reports/sales/export",
      { ...params, page: 1, per_page: 5000 },
      `sales-${from}-${to}.xlsx`,
    );
  }

  async function exportPdf() {
    await downloadReport("/api/reports/sales/pdf", params, `sales-${from}-${to}.pdf`);
  }

  return (
    <div className="space-y-6 p-6">
      <Link
        to="/reports"
        className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "gap-2 inline-flex")}
      >
        <ArrowLeft className="h-4 w-4" />
        Reports
      </Link>
      <h1 className="text-2xl font-semibold tracking-tight">Sales register</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {branchesEnabled ? (
              <div className="grid gap-1.5">
                <Label className="text-xs">Branch ID</Label>
                <Input value={branchId} onChange={(e) => setBranchId(e.target.value)} placeholder="Optional" />
              </div>
            ) : null}
            <div className="grid gap-1.5">
              <Label className="text-xs">Customer ID</Label>
              <Input value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="Optional" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">User ID</Label>
              <Input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="Optional" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs">Payment mode</Label>
              <Input
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                placeholder="cash, upi, …"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" onClick={() => void refetch()}>
              Apply
            </Button>
            <Button type="button" variant="outline" onClick={() => void exportXlsx()}>
              Excel
            </Button>
            <Button type="button" variant="outline" onClick={() => void exportPdf()}>
              PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {data?.summary && (
        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
          <span>Net ₹{data.summary.net.toFixed(2)}</span>
          <span>Taxable ₹{data.summary.taxable.toFixed(2)}</span>
          <span>CGST ₹{data.summary.cgst.toFixed(2)}</span>
          <span>SGST ₹{data.summary.sgst.toFixed(2)}</span>
          <span>IGST ₹{data.summary.igst.toFixed(2)}</span>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Results</CardTitle>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!data || page >= (data.pagination?.last_page ?? 1)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="overflow-x-auto rounded-md border max-h-[480px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="p-2 text-left">Bill</th>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Customer</th>
                    <th className="p-2 text-right">Net</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Offers</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.data ?? []).map((r) => (
                    <tr
                      key={String(r.id)}
                      className="border-t cursor-pointer hover:bg-muted/30"
                      onClick={() => navigate(`/sales/${String(r.id)}`)}
                    >
                      <td
                        className="p-2 font-mono"
                        onClick={(e) => {
                          // avoid double-trigger (row onClick)
                          e.stopPropagation();
                        }}
                      >
                        <Link className="text-primary hover:underline" to={`/sales/${String(r.id)}`}>
                          {String(r.bill_no ?? "")}
                        </Link>
                      </td>
                      <td className="p-2 whitespace-nowrap">{String(r.sale_date ?? "")}</td>
                      <td className="p-2 max-w-[160px] truncate">{String(r.customer_name ?? "—")}</td>
                      <td className="p-2 text-right tabular-nums">{Number(r.net_amount).toFixed(2)}</td>
                      <td className="p-2">{String(r.payment_status ?? "")}</td>
                      <td className="p-2">
                        {Number(r.offer_line_count ?? 0) > 0 ? (
                          <Badge variant="secondary" className="text-[10px] font-normal">
                            {Number(r.offer_line_count)} offer
                            {Number(r.offer_line_count) === 1 ? "" : "s"}
                          </Badge>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
